import { db } from '../db';
import { getProgress, getGenerateOutput, setReceived } from '../api-helper';
import electronBridge from '../utils/electronBridge';
import eventBus from '../utils/eventBus';
import FinishSound from '../sounds/finished.flac';

const ipcRenderer = electronBridge.ipcRenderer;

/**
 * Get media type from base64 string
 */
async function getMediaType(base64String) {
    try {
        const cleaned = base64String.trim().replace(/\s/g, '');
        const padded = cleaned + '==='.slice((cleaned.length + 3) % 4);
        const binaryStr = atob(padded);
        const bytes = new Uint8Array(binaryStr.length);

        for (let i = 0; i < binaryStr.length; i++) {
            bytes[i] = binaryStr.charCodeAt(i);
        }

        // Generate hex string from first 64 bytes for better detection
        const hex = Array.from(bytes.slice(0, 64))
            .map(b => b.toString(16).padStart(2, '0'))
            .join('');

        // Helper to check ASCII string at offset
        const asciiAt = (offset, str) => {
            if (offset + str.length > bytes.length) return false;
            for (let i = 0; i < str.length; i++) {
                if (bytes[offset + i] !== str.charCodeAt(i)) return false;
            }
            return true;
        };

        // RIFF-based formats need special handling (WAV, WEBP, AVI all start with RIFF)
        if (hex.startsWith('52494646') && bytes.length >= 12) {
            if (asciiAt(8, 'WAVE')) return "audio";
            if (asciiAt(8, 'WEBP')) return "image";
            if (asciiAt(8, 'AVI ')) return "video";
        }

        // MP3: ID3 tag or frame sync
        if (asciiAt(0, 'ID3') || (bytes[0] === 0xFF && (bytes[1] & 0xE0) === 0xE0)) {
            return "audio";
        }

        // FLAC: starts with "fLaC"
        if (asciiAt(0, 'fLaC')) {
            return "audio";
        }

        // AAC ADTS: starts with 0xFFF (12 bits)
        if (bytes[0] === 0xFF && (bytes[1] & 0xF0) === 0xF0) {
            return "audio";
        }

        // M4A/MP4: ftyp with brands
        if (bytes.length >= 12 && bytes[4] === 0x66 && bytes[5] === 0x74 && bytes[6] === 0x79 && bytes[7] === 0x70) {
            const brand = String.fromCharCode(bytes[8], bytes[9], bytes[10], bytes[11]);
            if (brand === 'M4A ' || brand === 'M4B ') {
                return "audio";
            }
            return "video";
        }

        // Other signatures
        const signatures = [
            { type: 'image/png', hex: '89504e47' },
            { type: 'image/jpeg', hex: 'ffd8ff' },
            { type: 'video/webm', hex: '1a45dfa3' },
            { type: 'audio/ogg', hex: '4f676753' },
            { type: 'application/pdf', hex: '25504446' },
            { type: 'model/gltf-binary', hex: '676c5446' },
        ];

        const matched = signatures.find(sig => hex.startsWith(sig.hex.toLowerCase()));
        if (matched) {
            const mimeType = matched.type;
            if (mimeType.startsWith("image/")) return "image";
            if (mimeType.startsWith("video/")) return "video";
            if (mimeType.startsWith("audio/")) return "audio";
            if (mimeType.startsWith("model/")) return "3d";
            return mimeType;
        }

        return "image"; // Default to image
    } catch (err) {
        console.error("Error parsing base64:", err);
        return "image";
    }
}

/**
 * Check and recover pending jobs after app refresh or navigation
 */
export async function checkForPendingJobs(apiState, generateStatusDispatch) {
    try {
        console.log('[JOB RECOVERY] Checking for pending jobs...');
        
        // Check localStorage for pending jobs
        const pendingJobsData = localStorage.getItem('pending_jobs');
        const legacyWorkload = localStorage.getItem('remember_workload');
        
        if (!pendingJobsData && !legacyWorkload) {
            console.log('[JOB RECOVERY] No pending jobs found');
            return;
        }

        let jobsToRecover = {};

        // Handle new multi-job format
        if (pendingJobsData) {
            try {
                const parsed = JSON.parse(pendingJobsData);
                if (parsed.jobs && typeof parsed.jobs === 'object') {
                    jobsToRecover = { ...parsed.jobs };
                }
            } catch (e) {
                console.error('[JOB RECOVERY] Error parsing pending_jobs:', e);
            }
        }

        // Handle legacy single job format
        if (legacyWorkload) {
            try {
                const workloadDetails = JSON.parse(legacyWorkload);
                if (workloadDetails?.active && workloadDetails?.uid) {
                    jobsToRecover[workloadDetails.uid] = {
                        uid: workloadDetails.uid,
                        count: workloadDetails.count || 1,
                        totalImages: workloadDetails.totalImage || 0,
                        timestamp: Date.now()
                    };
                }
            } catch (e) {
                console.error('[JOB RECOVERY] Error parsing legacy workload:', e);
            }
        }

        // Recover each pending job
        const jobIds = Object.keys(jobsToRecover);
        if (jobIds.length === 0) {
            console.log('[JOB RECOVERY] No valid jobs to recover');
            // Clear the localStorage
            localStorage.removeItem('pending_jobs');
            localStorage.removeItem('remember_workload');
            return;
        }

        console.log(`[JOB RECOVERY] Found ${jobIds.length} jobs to recover:`, jobIds);

        // Add each job back to the multi-job tracking
        for (const uid of jobIds) {
            const job = jobsToRecover[uid];
            console.log(`[JOB RECOVERY] Checking if job ${uid} should be recovered:`, job);
            
            // Check if this job already has results in the database
            const existingImages = await db.images.where('uid').equals(uid).toArray();
            
            // Categorize all images more thoroughly
            const completedImages = existingImages.filter(img => 
                img.image && img.image !== '' && 
                (img.dataType === 'image' || img.dataType === 'video' || img.dataType === 'audio')
            );
            const placeholders = existingImages.filter(img => 
                img.dataType === 'placeholder'
            );
            const failedImages = existingImages.filter(img => 
                (!img.image || img.image === '') && 
                (!img.dataType || img.dataType === '')
            );
            
            const totalProcessed = completedImages.length + failedImages.length;
            
            console.log(`[JOB RECOVERY] Job ${uid} status:
                - Completed: ${completedImages.length}
                - Placeholders: ${placeholders.length}
                - Failed: ${failedImages.length}
                - Total Processed: ${totalProcessed}
                - Expected: ${job.count}`);
            
            // Skip recovery if all images are accounted for (completed or failed)
            if (totalProcessed >= job.count) {
                console.log(`[JOB RECOVERY] Job ${uid} fully processed (${totalProcessed}/${job.count}), clearing from pending`);
                clearPendingJob(uid);
                continue;
            }
            
            // Also skip if we have more entries than expected (job definitely complete)
            if (existingImages.length >= job.count && placeholders.length === 0) {
                console.log(`[JOB RECOVERY] Job ${uid} has all expected entries with no placeholders, clearing from pending`);
                clearPendingJob(uid);
                continue;
            }
            
            // Double-check with server for jobs that seem incomplete
            const isCosyActive = uid.includes("-");
            try {
                const progressData = await getProgress(uid, isCosyActive, apiState, generateStatusDispatch);
                
                if (!progressData) {
                    console.log(`[JOB RECOVERY] Job ${uid} not found on server, clearing from pending`);
                    clearPendingJob(uid);
                    continue;
                }
                
                // Check if job is complete on server
                const serverProgress = progressData.percent || progressData.progress || 0;
                if (serverProgress >= 100 || progressData.complete === true) {
                    console.log(`[JOB RECOVERY] Server says job ${uid} is complete (progress: ${serverProgress}), clearing from pending`);
                    clearPendingJob(uid);
                    continue;
                }
                
                // Check for server errors
                if (progressData.error && progressData.error !== "None") {
                    console.log(`[JOB RECOVERY] Job ${uid} has error on server: ${progressData.error}, clearing from pending`);
                    clearPendingJob(uid);
                    continue;
                }
            } catch (error) {
                console.log(`[JOB RECOVERY] Could not verify job ${uid} with server:`, error);
                // Check job age as fallback
                const jobAge = Date.now() - (job.timestamp || 0);
                if (jobAge > 300000) { // 5 minutes
                    console.log(`[JOB RECOVERY] Job ${uid} is too old (${Math.round(jobAge/1000)}s), clearing`);
                    clearPendingJob(uid);
                    continue;
                }
            }
            
            // Add job to multi-job state
            generateStatusDispatch({
                type: 'addJob',
                uid: uid,
                count: job.count || 1
            });

            // Start recovery for this job
            eventBus.emit('recoverSingleJob', {
                uid,
                apiState,
                generateStatusDispatch,
                jobDetails: job
            });
        }

        // Clear legacy format after migration
        localStorage.removeItem('remember_workload');
        
    } catch (error) {
        console.error('[JOB RECOVERY] Fatal error in checkForPendingJobs:', error);
    }
}

/**
 * Recover a single job - this will be called by Generate.jsx
 */
export async function recoverSingleJob(uid, apiState, generateStatusDispatch, progressCallback) {
    console.log(`[JOB RECOVERY] Starting recovery for job ${uid}`);
    
    // Check if this job already has results before starting recovery
    const existingImages = await db.images.where('uid').equals(uid).toArray();
    const completedImages = existingImages.filter(img => img.image && img.image !== '');
    const failedImages = existingImages.filter(img => !img.image || img.image === '');
    
    // Get the expected count from pending jobs
    const pendingJobsData = localStorage.getItem('pending_jobs');
    let expectedCount = 1;
    if (pendingJobsData) {
        try {
            const pendingJobs = JSON.parse(pendingJobsData);
            if (pendingJobs.jobs && pendingJobs.jobs[uid]) {
                expectedCount = pendingJobs.jobs[uid].count || 1;
            }
        } catch (e) {
            console.error('[JOB RECOVERY] Error parsing pending jobs for count:', e);
        }
    }
    
    console.log(`[JOB RECOVERY] Pre-recovery check - Job ${uid}: Completed: ${completedImages.length}, Failed: ${failedImages.length}, Expected: ${expectedCount}`);
    
    // Skip recovery if job is already complete
    if (completedImages.length >= expectedCount) {
        console.log(`[JOB RECOVERY] Job ${uid} already has all images, skipping recovery`);
        clearPendingJob(uid);
        generateStatusDispatch({
            type: 'updateJob',
            uid,
            updates: { status: 'completed', progress: 100 }
        });
        setTimeout(() => {
            generateStatusDispatch({ type: 'removeJob', uid });
        }, 1000);
        return;
    }
    
    // Skip recovery if job already failed completely
    if (completedImages.length === 0 && failedImages.length >= expectedCount) {
        console.log(`[JOB RECOVERY] Job ${uid} already failed completely, skipping recovery`);
        clearPendingJob(uid);
        generateStatusDispatch({
            type: 'updateJob',
            uid,
            updates: { status: 'error', error: 'Job already failed' }
        });
        setTimeout(() => {
            generateStatusDispatch({ type: 'removeJob', uid });
        }, 1000);
        return;
    }
    
    let timeoutCount = 0;
    const maxTimeouts = 250; // About 10 minutes with 2.5s intervals
    let consecutiveErrors = 0;
    const maxConsecutiveErrors = 3;

    // Get settings
    let playSounds = false;
    try {
        const setting = await db.settings.where('key').equals('play_sounds').first();
        if (setting && setting.hasOwnProperty('value')) {
            playSounds = setting.value;
        }
    } catch (error) {
        console.error("Error checking play_sounds setting:", error);
    }

    // Get local directory for Electron saves
    let localDirectory = "";
    if (electronBridge.isElectron) {
        try {
            const dirSetting = await db.settings.where('key').equals('local_directory').first();
            if (dirSetting?.value) {
                localDirectory = dirSetting.value;
            }
        } catch (error) {
            console.error("Error getting local directory:", error);
        }
    }

    const pollJob = async () => {
        try {
            // Check if job was cancelled
            if (localStorage.getItem('isCancelling') === 'true') {
                console.log(`[JOB RECOVERY] Job ${uid} cancelled, stopping recovery`);
                clearPendingJob(uid);
                return;
            }

            timeoutCount++;
            if (timeoutCount >= maxTimeouts) {
                console.log(`[JOB RECOVERY] Job ${uid} timed out after ${maxTimeouts} attempts`);
                generateStatusDispatch({
                    type: 'updateJob',
                    uid,
                    updates: { status: 'error', error: 'Job recovery timed out' }
                });
                setTimeout(() => {
                    generateStatusDispatch({ type: 'removeJob', uid });
                }, 2000);
                clearPendingJob(uid);
                return;
            }

            // Determine if this is a Cosy job
            const isCosyActive = uid.includes("-");

            // Get progress for this job
            const progressData = await getProgress(uid, isCosyActive, apiState, generateStatusDispatch);
            
            if (!progressData) {
                consecutiveErrors++;
                if (consecutiveErrors >= maxConsecutiveErrors) {
                    console.error(`[JOB RECOVERY] Failed to get progress for ${uid} after ${maxConsecutiveErrors} attempts`);
                    clearPendingJob(uid);
                    return;
                }
                setTimeout(pollJob, 2500);
                return;
            }

            consecutiveErrors = 0; // Reset error counter on success

            console.log(`[JOB RECOVERY] Progress for ${uid}:`, progressData.progress);

            // Update progress
            const progress = progressData.percent || progressData.progress || 0;
            generateStatusDispatch({
                type: 'updateJob',
                uid,
                updates: { progress }
            });

            // Notify callback if provided
            if (progressCallback) {
                progressCallback(progressData);
            }

            // Check for errors
            if (progressData.error && progressData.error !== "None") {
                console.error(`[JOB RECOVERY] Job ${uid} has error:`, progressData.error);
                generateStatusDispatch({
                    type: 'updateJob',
                    uid,
                    updates: { status: 'error', error: progressData.error }
                });
                setTimeout(() => {
                    generateStatusDispatch({ type: 'removeJob', uid });
                }, 2000);
                clearPendingJob(uid);
                return;
            }

            // Process completed images
            if (progressData.output && Array.isArray(progressData.output)) {
                for (const item of progressData.output) {
                    const outputUid = item.uid;
                    if (!outputUid) continue;

                    try {
                        // Check if already processed
                        const hashcheck = await db.images.where('hash').equals(outputUid).first();
                        if (hashcheck) {
                            console.log(`[JOB RECOVERY] Image ${outputUid} already exists, skipping`);
                            continue;
                        }

                        // Get the actual image data
                        const image = await getGenerateOutput(uid, outputUid);
                        if (!image || !image.image) {
                            console.error(`[JOB RECOVERY] Failed to get image data for ${outputUid}`);
                            continue;
                        }

                        console.log(`[JOB RECOVERY] Retrieved image ${outputUid} for job ${uid}`);

                        // Determine media type
                        const dataType = await getMediaType(image.image);

                        // Save to file system if Electron
                        let savedFilePath = image.image;
                        if (electronBridge.isElectron && localDirectory) {
                            try {
                                savedFilePath = await ipcRenderer.invoke("test_upload", image.image, localDirectory, null, dataType);
                                console.log(`[JOB RECOVERY] Saved to file: ${savedFilePath}`);
                            } catch (error) {
                                console.error(`[JOB RECOVERY] Error saving to file:`, error);
                            }
                        }

                        // Save to database
                        await db.images.put({
                            image: savedFilePath,
                            seed: image.seed,
                            uid: uid,
                            model: image.model || '',
                            inCollection: false,
                            hash: outputUid,
                            online: apiState.online,
                            tag: '',
                            prompt: image.prompt,
                            dataType: dataType,
                            receivedtimestamp: Date.now(),
                            requestedtimestamp: Date.now()
                        });

                        // Mark as received
                        await setReceived(uid, outputUid);

                        // Update job progress
                        generateStatusDispatch({
                            type: 'incrementJobImages',
                            uid,
                            setting: playSounds
                        });

                        // Emit event for UI refresh
                        eventBus.emit('refreshImages');

                        console.log(`[JOB RECOVERY] Successfully recovered image ${outputUid}`);

                    } catch (error) {
                        console.error(`[JOB RECOVERY] Error processing image ${outputUid}:`, error);
                    }
                }
            }

            // Check if job is complete
            if (progress >= 100) {
                console.log(`[JOB RECOVERY] Job ${uid} completed successfully`);
                
                generateStatusDispatch({
                    type: 'updateJob',
                    uid,
                    updates: { status: 'completed', progress: 100 }
                });

                // Play completion sound
                if (playSounds) {
                    try {
                        const successAudio = new Audio(FinishSound);
                        successAudio.loop = false;
                        successAudio.volume = 0.4;
                        successAudio.play().catch(e => {
                            // Ignore audio play errors (usually due to browser autoplay policies)
                            console.log('[JOB RECOVERY] Could not play completion sound:', e.message);
                        });
                    } catch (error) {
                        console.log('[JOB RECOVERY] Error creating audio:', error);
                    }
                }

                // Clear from pending jobs
                clearPendingJob(uid);

                // Remove from UI after a delay
                setTimeout(() => {
                    generateStatusDispatch({ type: 'removeJob', uid });
                    eventBus.emit('refreshImages');
                }, 3000);

                return; // Stop polling
            }

            // Continue polling
            setTimeout(pollJob, 2500);

        } catch (error) {
            console.error(`[JOB RECOVERY] Error polling job ${uid}:`, error);
            consecutiveErrors++;
            
            if (consecutiveErrors >= maxConsecutiveErrors) {
                console.error(`[JOB RECOVERY] Stopping recovery for ${uid} after ${maxConsecutiveErrors} consecutive errors`);
                generateStatusDispatch({
                    type: 'updateJob',
                    uid,
                    updates: { status: 'error', error: 'Recovery failed due to repeated errors' }
                });
                clearPendingJob(uid);
                return;
            }
            
            // Try again
            setTimeout(pollJob, 5000); // Longer delay after error
        }
    };

    // Start polling
    pollJob();
}

/**
 * Save a job as pending (called when a new job starts)
 */
export function savePendingJob(uid, count, totalImages = 0) {
    try {
        const pendingJobsData = localStorage.getItem('pending_jobs');
        let pendingJobs = { jobs: {} };
        
        if (pendingJobsData) {
            try {
                pendingJobs = JSON.parse(pendingJobsData);
                if (!pendingJobs.jobs) {
                    pendingJobs.jobs = {};
                }
            } catch (e) {
                console.error('Error parsing existing pending_jobs:', e);
            }
        }

        // Add or update this job
        pendingJobs.jobs[uid] = {
            uid,
            count,
            totalImages,
            timestamp: Date.now()
        };

        localStorage.setItem('pending_jobs', JSON.stringify(pendingJobs));
        console.log(`[JOB RECOVERY] Saved pending job ${uid}`);
    } catch (error) {
        console.error('[JOB RECOVERY] Error saving pending job:', error);
    }
}

/**
 * Clear a specific pending job
 */
export function clearPendingJob(uid) {
    try {
        const pendingJobsData = localStorage.getItem('pending_jobs');
        if (!pendingJobsData) return;

        const pendingJobs = JSON.parse(pendingJobsData);
        if (pendingJobs.jobs && pendingJobs.jobs[uid]) {
            delete pendingJobs.jobs[uid];
            
            // Save updated list or remove entirely if empty
            if (Object.keys(pendingJobs.jobs).length === 0) {
                localStorage.removeItem('pending_jobs');
            } else {
                localStorage.setItem('pending_jobs', JSON.stringify(pendingJobs));
            }
            
            console.log(`[JOB RECOVERY] Cleared pending job ${uid}`);
        }

        // Also clear legacy format if it matches
        const legacyWorkload = localStorage.getItem('remember_workload');
        if (legacyWorkload) {
            try {
                const workloadDetails = JSON.parse(legacyWorkload);
                if (workloadDetails?.uid === uid) {
                    localStorage.setItem('remember_workload', JSON.stringify({
                        active: false,
                        uid: "",
                        count: 0,
                        totalImage: 0,
                        num: 0,
                    }));
                }
            } catch (e) {
                console.error('Error clearing legacy workload:', e);
            }
        }
    } catch (error) {
        console.error('[JOB RECOVERY] Error clearing pending job:', error);
    }
}

/**
 * Clear all pending jobs
 */
export function clearAllPendingJobs() {
    localStorage.removeItem('pending_jobs');
    localStorage.setItem('remember_workload', JSON.stringify({
        active: false,
        uid: "",
        count: 0,
        totalImage: 0,
        num: 0,
    }));
    console.log('[JOB RECOVERY] Cleared all pending jobs');
}